import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '@/components/Logo';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mic, X, MicOff, CheckCircle2, Brain, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { publicSupabase } from '@/integrations/supabase/client';
import { Conversation } from '@11labs/client';
import { createClient } from '@supabase/supabase-js';

type MessageEvent = {
  message: string;
  source: string;
};

// Create a Supabase client with the service role key for admin operations
const serviceRoleSupabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY
);

const styles = `
  @keyframes loading {
    0% {
      width: 0%;
    }
    50% {
      width: 100%;
    }
    100% {
      width: 0%;
    }
  }
  
  @keyframes blink {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(0.8);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.1);
      opacity: 1;
    }
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// Add useTypewriter hook at the top of the file after imports
const useTypewriter = (text: string, speed: number = 30) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  useEffect(() => {
    if (!text) {
      setDisplayText('');
      return;
    }
    
    setIsTyping(true);
    setDisplayText(''); // Reset display text
    
    let index = 0;
    
    const intervalId = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.substring(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(intervalId);
      }
    }, speed);
    
    return () => clearInterval(intervalId);
  }, [text, speed]);
  
  return { displayText, isTyping };
};

const AssessmentInterface: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const nameFromUrl = searchParams.get('name') || '';
  
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [messages, setMessages] = useState<Array<{ source: string; message: string }>>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [candidateName, setCandidateName] = useState<string>(nameFromUrl);
  const conversationRef = useRef<any>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const cleanupRef = useRef(false);
  const retryCountRef = useRef(0);
  const monitorIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_RETRIES = 3;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const lastStatusRef = useRef<string | null>(null);
  const [assessmentDetails, setAssessmentDetails] = useState<{
    skills?: string[];
    title?: string;
    custom_questions?: string[];
    organization_name?: string;
    organization_id?: string;
    voice_id?: string;
    language?: string;
    accent?: string;
    skill_questions?: string[] | Record<string, string[]>;
    assessmentType?: string;
    assessment_type?: string;
  }>({});
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [aiEndedConversation, setAiEndedConversation] = useState(false);
  const socket = useRef<WebSocket | null>(null);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [isHandlingCompletion, setIsHandlingCompletion] = useState(false);
  const [isAssessmentCompleted, setIsAssessmentCompleted] = useState(false);
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
  const [completionStatus, setCompletionStatus] = useState<'confirming' | 'processing' | 'completed'>('confirming');

  // Slow down typing speed by 25% (from 30ms to 40ms)
  const { displayText: animatedQuestion, isTyping: isQuestionTyping } = useTypewriter(currentQuestion || '', 40);

  // Add state for candidate ID
  const [candidateId, setCandidateId] = useState<string>('');

  // Add useEffect to fetch candidate details
  useEffect(() => {
    const fetchCandidateDetails = async () => {
      if (!email || !id) return;
      
      try {
        const { data: candidate, error } = await publicSupabase
          .from('candidates')
          .select('id, name')
          .eq('email', email)
          .eq('assessment_id', id)
          .single();
          
        if (error) throw error;
        if (candidate) {
          if (candidate.name) setCandidateName(candidate.name);
          if (candidate.id) setCandidateId(candidate.id);
        }
      } catch (error) {
        console.error('Error fetching candidate details:', error);
      }
    };
    
    fetchCandidateDetails();
  }, [email, id]);

  // Add useEffect to fetch assessment details
  useEffect(() => {
    const fetchAssessmentDetails = async () => {
      if (!id) return;
      
      try {
        // First get the assessment with organization_id and voice settings
        const { data, error } = await publicSupabase
          .from('assessments')
          .select('skills, title, custom_questions, organization_id, voice_id, language, accent, skill_questions, assessment_type')
          .eq('id', id)
          .single();
        
        if (error) throw error;
        if (data) {
          // Store the assessment details first with organization_id
          setAssessmentDetails({
            ...data,
            organization_id: data.organization_id,
            assessment_type: data.assessment_type // Ensure assessment_type is explicitly set
          });
          
          // If there's an organization_id, fetch the organization name
          if (data.organization_id) {
            const { data: orgData, error: orgError } = await publicSupabase
              .from('organizations')
              .select('name')
              .eq('id', data.organization_id)
              .single();
              
            if (!orgError && orgData && orgData.name) {
              // Update assessment details with organization name
              setAssessmentDetails(prev => ({
                ...prev,
                organization_name: orgData.name
              }));
            } else {
              console.warn('Organization not found or error fetching:', orgError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching assessment details:', error);
      }
    };

    fetchAssessmentDetails();
  }, [id]);

  // Cleanup function to stop monitoring and reset state
  const stopConnectionMonitor = useCallback(() => {
    if (monitorIntervalRef.current) {
      clearInterval(monitorIntervalRef.current);
      monitorIntervalRef.current = null;
    }
  }, []);

  const initializeMicrophone = async (stream: MediaStream) => {
    try {
      // Ensure all audio tracks are initially enabled
      const audioTracks = stream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = true;
      });
      
      // Set UI state to match (unmuted)
      setIsMuted(false);
    } catch (error) {
      // Handle error silently
    }
  };
  
  const startInterview = useCallback(async () => {
    try {
      if (isConnecting) {
        return;
      }

      // Request microphone permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!stream) {
          toast.error("Microphone permission is required for the conversation.");
          return;
        }
        audioStreamRef.current = stream;
        
        // Initialize microphone (ensure it's unmuted at the start)
        await initializeMicrophone(stream);
      } catch (error) {
        toast.error("Microphone permission is required for the conversation.");
        return;
      }

      setIsConnecting(true);

      // Fixed agent ID
      const agentId = "OpVJKkSOPLl3s83MHXVg";

      // Start the conversation session
      // Language code mapping from language_accents.csv
      const languageCodeMap: Record<string, string> = {
        'Arabic': 'ar',
        'Chinese': 'zh',
        'English': 'en',
        'French': 'fr',
        'German': 'de',
      };

      const languageName = assessmentDetails?.language || 'English';
      const languageCode = languageCodeMap[languageName] || 'en';
      const roleName = assessmentDetails?.title || 'Technical Assessment';
      const companyName = assessmentDetails?.organization_name || 'RoundOne';
      const candidate = candidateName || 'Candidate';
      
      // Generate prompts using the centralized prompt generator
      let prompt = '';
      let firstMessage = '';
      
      if (candidateId) {
        try {
          const promptResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/promptsgenerator`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              assessment: {
                id: id,
                title: assessmentDetails?.title || roleName,
                skills: assessmentDetails?.skills,
                custom_questions: assessmentDetails?.custom_questions,
                skill_questions: assessmentDetails?.skill_questions,
                language: assessmentDetails?.language,
                accent: assessmentDetails?.accent,
                voice_id: assessmentDetails?.voice_id,
                assessment_type: assessmentDetails?.assessment_type,
                organizations: { name: companyName }
              },
              candidate: {
                id: candidateId,
                name: candidate,
                email: email
              },
              callType: 'callback'
            })
          });

          if (promptResponse.ok) {
            const promptData = await promptResponse.json();
            prompt = promptData.prompt;
            firstMessage = promptData.firstMessage;
          } else {
            throw new Error('Failed to generate prompts');
          }
                 } catch (error) {
           console.error('Failed to use centralized prompt generator:', error);
           toast.error('Failed to generate assessment prompts. Please try again.');
           setIsConnecting(false);
           return;
         }
       } else {
         console.error('Candidate ID not available');
         toast.error('Unable to start assessment. Please refresh the page.');
         setIsConnecting(false);
         return;
       }

      const conversation = await Conversation.startSession({
        agentId: agentId,
        dynamicVariables: {
          assessment_id: id || '',
          candidate_id: candidateId || '',
          candidate_name: candidate || '',
          candidate_email: email || '',
          assessment_title: roleName || '',
          company_name: companyName || '',
          skills: Array.isArray(assessmentDetails?.skills) ? assessmentDetails.skills.join(', ') : assessmentDetails?.skills || '',
          role_name: roleName || '',
          language: assessmentDetails?.language || 'English',
          accent: assessmentDetails?.accent || '',
          voice_id: assessmentDetails?.voice_id || ''
        },
        overrides: {
          agent: {
            prompt: { prompt },
            firstMessage,
            language: languageCode as any,
          },
          tts: { 
            ...(assessmentDetails?.voice_id ? { voiceId: assessmentDetails.voice_id } : {})
          },
          asr: {
            model: "flash_v2.5"
          }
        } as any,
        onConnect: () => {
          setIsConnecting(false);
          setHasStarted(true);
          retryCountRef.current = 0;
          toast.success('Connected to interviewer');
        },
        onDisconnect: () => {
          // If we're already handling completion or cleanup, don't do anything
          if (cleanupRef.current || isHandlingCompletion || isAssessmentCompleted) {
            return;
          }

          setIsConnecting(false);
          
          // Only set hasStarted to false if we're not completing the assessment
          if (!isAssessmentCompleted) {
            setHasStarted(false);
          }
          
          setIsSpeaking(false);

          // Check if this was a natural end to the conversation
          const lastMessage = messages[messages.length - 1];
          const isNaturalEnd = lastMessage?.source === 'ai' && (
            lastMessage.message.toLowerCase().includes('thank you') ||
            lastMessage.message.toLowerCase().includes('best of luck') ||
            lastMessage.message.toLowerCase().includes('wish you') ||
            lastMessage.message.toLowerCase().includes('end this call')
          );

          if (isNaturalEnd) {
            setIsHandlingCompletion(true);
            setIsAssessmentCompleted(true);
            // Add a small delay to ensure all states are updated
            setTimeout(() => {
              handleConfirmEnd();
            }, 1000);
            return;
          }

          // If not a natural end and we haven't exceeded retries, attempt reconnection
          if (hasStarted && retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current += 1;
            
            setTimeout(() => {
              if (!cleanupRef.current && !isConnecting) {
                startInterview().catch(error => {
                  toast.error('Failed to reconnect. Please try again.');
                });
              }
            }, 2000);
          } else if (retryCountRef.current >= MAX_RETRIES) {
            toast.error('Connection lost. Please refresh the page to try again.');
          }
        },
        onError: (error) => {
          toast.error('An error occurred during the conversation.');
          setIsConnecting(false);
          setHasStarted(false);
        },
        onMessage: (props) => {
          // Only process if we have a valid message
          if (!props.message) return;
          
          // Set speaking state based on message source
          setIsSpeaking(props.source === "ai");
          
          // Format message with candidate name
          const newMessage = {
            contentType: props.source === "ai" ? "Q" : "A",
            operator: "RoundOne AI",
            userName: props.source === "user" ? candidateName || "Candidate" : "RoundOne AI",
            content: props.message,
            qaTime: Date.now(),
          };

          // Update messages state with source being either "ai" or the candidate's name
          setMessages(prevMessages => [...prevMessages, { 
            source: props.source === "user" ? candidateName || "Candidate" : "ai", 
            message: props.message 
          }]);

          // Update transcript with delay for AI messages
          if (props.source === "ai") {
            // Clean the message to remove any undefined or unexpected content
            let cleanedMessage = String(props.message);
            cleanedMessage = cleanedMessage.replace(/undefined$/, '');
            setCurrentQuestion(cleanedMessage);
            
            // Add delay for AI messages for smoother UI updates
            setTimeout(() => {
              setTranscriptText(prev => prev + `${newMessage.userName}: ${cleanedMessage}\n`);
              // Scroll to bottom after AI message
              const transcriptElement = document.querySelector('.transcript-container');
              if (transcriptElement) {
                transcriptElement.scrollTop = transcriptElement.scrollHeight;
              }
            }, 1000);
          } else {
            // Update immediately for user messages
            setTranscriptText(prev => prev + `${newMessage.userName}: ${props.message}\n`);
            // Scroll to bottom immediately for user messages
            setTimeout(() => {
              const transcriptElement = document.querySelector('.transcript-container');
              if (transcriptElement) {
                transcriptElement.scrollTop = transcriptElement.scrollHeight;
              }
            }, 100);
          }
        },
        onModeChange: ({ mode }) => {
          setIsSpeaking(mode === "speaking");
        }
      });

      conversationRef.current = conversation;
      
      // Try to get conversation ID after conversation is created
      if (conversation) {
        // Get the conversation ID from the connection object
        const convId = (conversation as any)?.connection?.conversationId;
        if (convId) {
          setConversationId(convId);
        }
      }

    } catch (error) {
      toast.error('Failed to start conversation. Please try again.');
      setIsConnecting(false);
      setHasStarted(false);
    }
  }, [isConnecting, email, hasStarted, candidateName, assessmentDetails, isAssessmentCompleted]);

  // Connection monitor
  useEffect(() => {
    if (hasStarted && !cleanupRef.current) {
      monitorIntervalRef.current = setInterval(() => {
        // Monitor connection (removed logging)
      }, 5000);
    }

    return () => {
      stopConnectionMonitor();
    };
  }, [hasStarted, stopConnectionMonitor]);

  // Add new effect to handle AI ending the conversation
  useEffect(() => {
    if (aiEndedConversation && !cleanupRef.current) {
      handleConfirmEnd();
    }
  }, [aiEndedConversation]);

  const handleConfirmEnd = async () => {
    if (cleanupRef.current) {
      return;
    }
    
    try {
      cleanupRef.current = true;
      setIsAssessmentCompleted(true);
      setCompletionStatus('processing');
      // Hide the confirmation dialog and show the processing screen
      setShowEndDialog(false);
      setShowCompletionScreen(true);
      stopConnectionMonitor();
      
      // First, stop all audio tracks to immediately stop any interaction
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        audioStreamRef.current = null; // Clear the reference
      }
      
      // End the conversation session
      if (conversationRef.current) {
        try {
          await conversationRef.current.endSession();
        } catch (endSessionError) {
          // Continue with the process even if ending the conversation fails
        } finally {
          // Clear the conversation reference to prevent further interactions
          conversationRef.current = null;
        }
      }
      
      // Get conversation ID from state and immediately clear it to prevent duplicates
      const currentConversationId = conversationId;
      setConversationId(null);

      // Update candidate record with conversation ID
      if (currentConversationId) {
        try {
          const { error: updateError } = await serviceRoleSupabase
            .from('candidates')
            .update({ conversation_id: currentConversationId })
            .eq('assessment_id', id)
            .eq('email', email);

          if (updateError) {
            console.error('[END ASSESSMENT] Error updating candidate conversation ID:', updateError);
          }
        } catch (error) {
          console.error('[END ASSESSMENT] Exception updating candidate conversation ID:', error);
        }
      }
      
      // Format transcript with timestamps - deep copy to prevent issues
      const timestampedTranscript = JSON.parse(JSON.stringify(
        messages.map(msg => ({
          source: msg.source,
          message: msg.message,
          timestamp: new Date().toISOString()
        }))
      ));
      
      // Pause here briefly to ensure UI updates are processed - REDUCE THIS DELAY
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Save assessment results to Supabase
      if (id && email) {
        setIsProcessingAudio(true);
        
        // Add a timeout for the function call
        let functionCallTimeout: NodeJS.Timeout | null = null;
        const timeoutPromise = new Promise<{error: true, message: string}>((resolve) => {
          functionCallTimeout = setTimeout(() => {
            resolve({
              error: true,
              message: 'Assessment submission is taking longer than expected. You can close this page now, your responses have been saved.'
            });
          }, 15000); // 15 second timeout
        });
        
        try {
          // Get the candidate details
          const { data: candidateData, error: candidateError } = await serviceRoleSupabase
            .from('candidates')
            .select('id, status, name')
            .eq('assessment_id', id)
            .eq('email', email)
            .single();

          if (candidateError) {
            throw candidateError;
          }

          if (!candidateData?.id) {
            throw new Error('Candidate not found');
          }
          
          // Get the assessment details for skills
          const { data: assessmentData, error: assessmentError } = await serviceRoleSupabase
            .from('assessments')
            .select('organization_id, skills, title, organization:organizations(name)')
            .eq('id', id)
            .single();

          if (assessmentError) {
            throw assessmentError;
          }

          // Prepare data to send to the complete-assessment function
          const requestData = {
            phoneNumber: null,
            candidateName: candidateData.name || nameFromUrl,
            skills: Array.isArray(assessmentData.skills) ? assessmentData.skills.join(',') : assessmentData.skills,
            jobPost: id, // assessment_id
            candidateID: candidateData.id,
            company: assessmentData.organization ? (assessmentData.organization as any).name || '' : '',
            role: assessmentData.title || '',
            questionAnswersArray: timestampedTranscript.map((item: { source: string; message: string; timestamp: string }) => ({
              contentType: item.source === 'ai' ? 'Q' : 'A',
              content: item.message,
              timestamp: item.timestamp
            })),
            elevenlabsconversationId: currentConversationId
          };

          // Call the complete-assessment function with timeout
          const completionPromise = serviceRoleSupabase.functions.invoke('complete-assessment', {
            body: requestData
          });
          
          // Race between the function call and the timeout
          const result = await Promise.race([completionPromise, timeoutPromise]);
          
          // Clear timeout if function call completed before timeout
          if (functionCallTimeout) clearTimeout(functionCallTimeout);
          
          // Check if result is from timeout
          if (result && typeof result === 'object' && 'error' in result && result.error === true && 'message' in result) {
            toast.success(result.message as string, { duration: 10000 });
            setShowEndDialog(false);
            setShowCompletionScreen(true);
            setCompletionStatus('completed');
            return;
          }
          
          // Continue with normal processing for successful function call
          const { data: completionData, error: completionError } = result as any;

          if (completionError) {
            throw completionError;
          }

          setShowEndDialog(false);
          setShowCompletionScreen(true);
          setCompletionStatus('completed');
          
        } catch (error) {
          toast.error('Failed to complete assessment. Please try again.');
          setIsAssessmentCompleted(false);
          setCompletionStatus('confirming');
        } finally {
          setIsProcessingAudio(false);
        }
      }
    } catch (error) {
      toast.error('Failed to end assessment. Please try again.');
      setIsAssessmentCompleted(false);
      setCompletionStatus('confirming');
    }
  };

  // Function to mute/unmute
  const toggleMute = async () => {
    try {
      if (audioStreamRef.current) {
        const audioTracks = audioStreamRef.current.getAudioTracks();
        // Toggle the enabled state of all audio tracks (microphone)
        audioTracks.forEach(track => {
          // If currently not muted (enabled), then disable it (mute)
          // If currently muted (disabled), then enable it (unmute)
          track.enabled = !track.enabled;
        });
        
        // Toggle the muted state in UI to reflect actual microphone state
        const newMutedState = !audioTracks[0]?.enabled;
        setIsMuted(newMutedState);
        
        // Show toast notification
        toast.success(newMutedState ? 'Microphone muted' : 'Microphone enabled', {
          position: 'bottom-right',
          duration: 2000,
        });
      }
    } catch (error) {
      toast.error('Failed to toggle microphone');
    }
  };

  // Handle volume change (removing this as we're not controlling speaker volume)
  const handleVolumeChange = async (newVolume: number[]) => {
    // This function is no longer used
  };

  // Cleanup function to stop all tracks when component unmounts
  useEffect(() => {
    return () => {
      cleanupRef.current = true;
      stopConnectionMonitor();

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (conversationRef.current) {
        conversationRef.current.endSession().catch(console.error);
      }
    };
  }, [stopConnectionMonitor]);

  // Handle beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanupRef.current = true;
      stopConnectionMonitor();
      if (conversationRef.current) {
        conversationRef.current.endSession().catch(console.error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [stopConnectionMonitor]);

  useEffect(() => {
    if (!socket.current) return;

    socket.current.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'status') {
          const status = data.status.toLowerCase();
          if (status !== lastStatusRef.current) {
            lastStatusRef.current = status;
            if (status === 'speaking') {
              setIsSpeaking(true);
            } else {
              setIsSpeaking(false);
            }
          }
        } else if (data.type === 'message') {
          const message = data.message;
          setMessages(prev => [...prev, { source: 'ai', message }]);
          
          // Clean the message
          let cleanedMessage = String(message);
          cleanedMessage = cleanedMessage.replace(/undefined$/, '');
          setCurrentQuestion(cleanedMessage);
          
          // Check if the AI's message indicates the end of the assessment
          const lowerMessage = cleanedMessage.toLowerCase();
          if (lowerMessage.includes('assessment complete') || 
              lowerMessage.includes('assessment concluded') ||
              lowerMessage.includes('thank you for completing') ||
              lowerMessage.includes('this concludes the assessment') ||
              lowerMessage.includes('we have completed all the questions') ||
              lowerMessage.includes('we can end this call')) {
            if (!isHandlingCompletion && !cleanupRef.current) {
              setIsHandlingCompletion(true);
              // Add a small delay to ensure the last message is saved
              setTimeout(() => {
                handleConfirmEnd();
              }, 1000);
            }
          }
        }
      } catch (error) {
        // Handle silently
      }
    };

    // Add WebSocket close handler
    socket.current.onclose = () => {
      // If the WebSocket closes and we haven't handled completion yet, handle it
      if (hasStarted && !cleanupRef.current && !isHandlingCompletion) {
        setIsHandlingCompletion(true);
        handleConfirmEnd();
      }
    };

    return () => {
      if (socket.current) {
        socket.current.onmessage = null;
        socket.current.onclose = null;
      }
    };
  }, [socket.current, hasStarted, isHandlingCompletion]);

  // Auto-scroll effect when new messages appear
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Add a console log to check when the End Assessment button is clicked
  const handleEndAssessmentClick = () => {
    setShowEndDialog(true);
  };

  return (
    <>
      <style>{styles}</style>
      {showCompletionScreen ? (
        <div className="fixed inset-0 z-50 bg-background/95 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <Card className="border-primary/20 shadow-xl">
              <CardContent className="pt-8 pb-6 space-y-8">
                <div className="text-center space-y-4">
                  {completionStatus === 'processing' ? (
                    <div className="relative w-28 h-28 mx-auto">
                      {/* Processing animation */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin" style={{ 
                          width: '100%', 
                          height: '100%', 
                          border: '3px dashed hsl(var(--roundone-purple))',
                          borderRadius: '50%',
                          animationDuration: '12s'
                        }}></div>
                      </div>
                      
                      <div 
                        className="absolute inset-0 flex items-center justify-center"
                        style={{
                          animationName: 'pulse',
                          animationDuration: '3s',
                          animationTimingFunction: 'ease-in-out',
                          animationIterationCount: 'infinite'
                        }}
                      >
                        <Brain className="text-[hsl(var(--roundone-purple))] h-14 w-14" />
                      </div>
                      
                      <div className="absolute inset-0 flex items-center justify-center animate-ping opacity-30">
                        <Sparkles className="text-[hsl(var(--roundone-light-purple))] h-8 w-8" />
                      </div>
                    </div>
                  ) : (
                    <div className="mx-auto relative w-28 h-28">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-pulse bg-green-100 w-full h-full rounded-full"></div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <CheckCircle2 className="h-16 w-16 text-green-500" strokeWidth={1.5} />
                      </div>
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold">
                    {completionStatus === 'processing' ? 'Processing Your Assessment' : 'Assessment Complete!'}
                  </h3>
                  
                  <div className="text-muted-foreground">
                    {completionStatus === 'processing' ? (
                      <div className="space-y-2 text-left p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-primary animate-pulse"></div>
                          <span className="block text-sm">Saving your responses...</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-primary/70 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                          <span className="block text-sm">Processing the conversation...</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-primary/40 animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                          <span className="block text-sm">Analyzing your performance...</span>
                        </div>
                        <div className="mt-4 text-primary/80 font-medium text-center text-sm">
                          Please do not close your browser.
                        </div>
                      </div>
                    ) : (
                      <div className="text-center space-y-4">
                        <div className="text-lg text-primary font-medium">Thank you for completing the assessment!</div>
                        <div className="text-muted-foreground text-sm mt-2 bg-muted/30 p-4 rounded-lg">
                          Your responses have been recorded successfully. The recruitment team will review your assessment and get back to you via email with the next steps.
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress animation */}
                {completionStatus === 'processing' && (
                  <div className="relative max-w-md mx-auto">
                    <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full"
                        style={{
                          width: '0%',
                          animation: 'pulse 3s infinite ease-in-out',
                          animationName: 'loading',
                          animationDuration: '8s',
                          animationIterationCount: 'infinite',
                          animationTimingFunction: 'ease-in-out'
                        }}
                      ></div>
                    </div>
                    <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                      <span>Processing</span>
                      <span>Please wait...</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="min-h-screen flex flex-col">
          {/* Header */}
          <header className="border-b py-4 px-6 flex justify-between items-center bg-white">
            <div className="flex-1">
              <Logo size="sm" />
            </div>
            <div className="flex-1 text-center">
              <h1 className="text-base font-semibold">
                {assessmentDetails?.title ? assessmentDetails.title : 'Assessment'}
              </h1>
            </div>
            <div className="flex-1 flex justify-end items-center gap-4">
              {hasStarted && !isAssessmentCompleted && (
                <Button 
                  variant="destructive" 
                  onClick={handleEndAssessmentClick}
                  className="text-sm"
                  size="sm"
                  disabled={isHandlingCompletion}
                >
                  End Assessment
                </Button>
              )}
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 p-4 max-w-6xl mx-auto w-full">
            {!hasStarted && !isAssessmentCompleted && (
              <div className="flex flex-col gap-10 mb-8 mt-12">
                <div className="max-w-4xl mx-auto text-center">
                  <h1 className="text-3xl font-bold mb-2">Welcome to your assessment</h1>
                  <p className="text-lg text-muted-foreground">Click the button below to begin your preliminary screening</p>
                </div>

                <div className="max-w-lg mx-auto w-full text-center">
                  <Button 
                    onClick={startInterview}
                    disabled={isConnecting}
                    size="lg"
                    className="bg-[hsl(var(--roundone-purple))] hover:bg-[hsl(var(--roundone-purple))]/90 text-white font-medium px-16 py-6 text-lg"
                  >
                    {isConnecting ? (
                      <>
                        <span className="mr-2">Connecting</span>
                        <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      </>
                    ) : (
                      <>
                        <Mic className="mr-2 h-5 w-5" />
                        Start Assessment
                      </>
                    )}
                  </Button>
                </div>

                <div className="max-w-lg mx-auto w-full">
                  <Card className="border shadow-sm">
                    <CardContent className="pt-6">
                      <h2 className="text-lg font-semibold mb-4 flex items-center">
                        <CheckCircle2 className="h-5 w-5 mr-2 text-[hsl(var(--roundone-purple))]" />
                        Before you begin:
                      </h2>
                      <ul className="space-y-3">
                        <li className="flex items-center">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center mr-3">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--roundone-purple))]" />
                          </div>
                          <span className="text-muted-foreground">Ensure you're in a quiet environment</span>
                        </li>
                        <li className="flex items-center">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center mr-3">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--roundone-purple))]" />
                          </div>
                          <span className="text-muted-foreground">Check that your microphone is working</span>
                        </li>
                        <li className="flex items-center">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center mr-3">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--roundone-purple))]" />
                          </div>
                          <span className="text-muted-foreground">Have your resume handy for reference</span>
                        </li>
                        <li className="flex items-center">
                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center mr-3">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--roundone-purple))]" />
                          </div>
                          <span className="text-muted-foreground">Set aside approximately 5-10 minutes</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Current Question Card */}
            {hasStarted && currentQuestion && (
              <div className="mb-4 mt-4">
                <Card className="border shadow-sm">
                  <CardContent className="py-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-[hsl(var(--roundone-purple))]/80">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z" />
                        </svg>
                        <span className="text-sm font-medium">Current Question</span>
                      </div>
                      <p className="text-lg font-medium leading-relaxed min-h-[3.5rem]">
                        {animatedQuestion}
                        {isQuestionTyping && (
                          <span className="inline-block w-1.5 h-4 ml-0.5 bg-[hsl(var(--roundone-purple))]/80 animate-blink"></span>
                        )}
                      </p>
                      {isSpeaking && (
                        <div className="flex items-center gap-2 text-[hsl(var(--roundone-purple))] mt-2 bg-[hsl(var(--roundone-purple))]/5 p-2 rounded-lg inline-flex">
                          <div className="relative flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-[hsl(var(--roundone-purple))] rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-[hsl(var(--roundone-purple))] rounded-full animate-bounce [animation-delay:0.2s]"></div>
                            <div className="w-1.5 h-1.5 bg-[hsl(var(--roundone-purple))] rounded-full animate-bounce [animation-delay:0.4s]"></div>
                          </div>
                          <span className="text-sm font-medium">AI is speaking...</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Transcript Section */}
            {hasStarted && (
              <Card className="border shadow-sm mb-4">
                <CardContent className="p-4 h-[calc(100vh-22rem)]">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-[hsl(var(--roundone-purple))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h2 className="font-semibold">Conversation Transcript</h2>
                  </div>
                  <div className="h-full overflow-y-auto bg-muted/20 p-4 rounded-md transcript-container custom-scrollbar" ref={transcriptContainerRef}>
                    <div className="space-y-4">
                      {messages.map((msg, index) => (
                        <div 
                          key={index} 
                          className={`flex ${msg.source === 'ai' ? 'justify-start' : 'justify-end'}`}
                        >
                          <div 
                            className={`max-w-[80%] p-3 rounded-xl ${
                              msg.source === 'ai' 
                                ? 'bg-white border border-muted shadow-sm' 
                                : 'bg-[hsl(var(--roundone-purple))] text-white'
                            }`}
                          >
                            <div className="text-xs mb-1 opacity-70">
                              {msg.source === 'ai' ? 'AI Interviewer' : 'You'}
                            </div>
                            <div className="text-sm">{msg.message}</div>
                          </div>
                        </div>
                      ))}
                      {!messages.length && (
                        <div className="text-center text-muted-foreground py-8">
                          <div className="mb-2">
                            <svg className="w-8 h-8 mx-auto opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <p className="text-sm">The conversation transcript will appear here...</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      )}

      {/* Enhanced End Assessment Dialog with better feedback */}
      <AlertDialog open={showEndDialog} onOpenChange={(open) => {
        // Only allow closing if we're not already processing
        if (!isHandlingCompletion) {
          setShowEndDialog(open);
        }
      }}>
        <AlertDialogContent className="max-w-md border-primary/20 shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-center mb-2">
              {completionStatus === 'confirming' ? 'End Assessment' : 'Assessment Completion'}
            </AlertDialogTitle>
            {completionStatus === 'confirming' ? (
              <>
                <AlertDialogDescription className="text-center">
                  Are you sure you want to end this assessment?
                </AlertDialogDescription>
                <div className="mt-4 p-4 bg-muted/30 rounded-lg text-center">
                  <div className="text-sm text-muted-foreground">
                    Your responses will be saved and the assessment will be marked as completed.
                    <br />
                    <span className="text-[hsl(var(--roundone-purple))] font-medium mt-2 block">This action cannot be undone.</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center space-y-8 py-8">
                {/* Processing Animation */}
                <div className="relative w-32 h-32">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: '3px dashed hsl(var(--roundone-purple))',
                      borderRadius: '50%',
                      animation: 'spin 12s linear infinite'
                    }}></div>
                  </div>
                  
                  <div 
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      animation: 'pulse 3s ease-in-out infinite'
                    }}
                  >
                    <Brain className="text-[hsl(var(--roundone-purple))] h-14 w-14" />
                  </div>
                  
                  <div className="absolute inset-0 flex items-center justify-center animate-ping opacity-30">
                    <Sparkles className="text-[hsl(var(--roundone-light-purple))] h-8 w-8" />
                  </div>
                </div>

                <div className="space-y-4 text-center">
                  <div className="text-xl font-semibold text-[hsl(var(--roundone-purple))] animate-pulse">
                    {completionStatus === 'processing' ? (
                      'Processing Your Assessment'
                    ) : (
                      'Assessment Completed Successfully!'
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground max-w-sm">
                    {completionStatus === 'processing' ? (
                      'Please wait while we save and analyze your responses...'
                    ) : (
                      'Thank you for completing the assessment. The recruitment team will get back to you via email soon.'
                    )}
                  </div>
                </div>

                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[hsl(var(--roundone-purple))] rounded-full"
                    style={{
                      width: '0%',
                      animation: 'loading 8s ease-in-out infinite'
                    }}
                  ></div>
                </div>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            {completionStatus === 'confirming' && (
              <>
                <AlertDialogCancel 
                  className="hover:bg-muted/80" 
                  disabled={isHandlingCompletion}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => {
                    setIsHandlingCompletion(true); // Set this first to prevent multiple clicks
                    // Force clean state to ensure we can process the end
                    cleanupRef.current = false;
                    handleConfirmEnd();
                  }}
                  className="bg-[hsl(var(--roundone-purple))] text-white hover:bg-[hsl(var(--roundone-purple))]/90 shadow-md hover:shadow-lg transition-all"
                  disabled={isHandlingCompletion}
                >
                  {isHandlingCompletion ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-white border-opacity-30 border-t-white rounded-full"></div>
                      Processing...
                    </div>
                  ) : (
                    'End Assessment'
                  )}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add these styles to your existing styles */}
      <style>{`
        ${styles}
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default AssessmentInterface;



